import { useEffect, useState } from "react";
import { Check, MapPin, XCircle } from "lucide-react";
import {
  listMyObservations,
  type FieldObservationOut,
} from "../api/fieldPractice";
import { formatMoscowDate, formatMoscowDateTime } from "../utils/datetime";

export function FieldPracticeSection() {
  const [items, setItems] = useState<FieldObservationOut[] | null>(null);

  useEffect(() => {
    void listMyObservations()
      .then(setItems)
      .catch(() => setItems([]));
  }, []);

  if (items === null) {
    return (
      <section className="card mt-4 p-5">
        <div className="h-3 w-32 animate-pulse rounded bg-surface-alt" />
        <div className="mt-3 h-16 animate-pulse rounded-lg bg-surface-alt" />
      </section>
    );
  }
  if (items.length === 0) {
    return null;
  }

  const done = items.filter((o) => o.status === "completed").length;

  return (
    <section className="card mt-4 p-5">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
          Полевая практика
        </div>
        <div className="text-xs text-ink-muted">
          <span className="font-semibold tabular-nums text-ink">{done}</span>{" "}
          из <span className="tabular-nums">{items.length}</span>
        </div>
      </div>

      <ul className="mt-3 space-y-2">
        {items.map((o, idx) => (
          <ObservationRow key={o.id} item={o} index={idx} />
        ))}
      </ul>
    </section>
  );
}

function ObservationRow({
  item: o,
  index,
}: {
  item: FieldObservationOut;
  index: number;
}) {
  const isDone = o.status === "completed";
  const isCancelled = o.status === "cancelled";
  const schedule = o.scheduled_at
    ? formatMoscowDateTime(o.scheduled_at, {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;
  const completed = o.completed_at
    ? formatMoscowDate(o.completed_at)
    : null;

  return (
    <li className="flex items-start gap-3 rounded-lg bg-surface-alt/40 px-3 py-2.5">
      <span
        className={`mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full ${
          isCancelled
            ? "bg-surface-alt text-ink-muted"
            : isDone
              ? "bg-brand-success text-white"
              : "bg-brand/10 text-brand"
        }`}
      >
        {isDone ? (
          <Check size={14} strokeWidth={2.5} />
        ) : isCancelled ? (
          <XCircle size={14} strokeWidth={2} />
        ) : (
          <span className="text-[11px] font-semibold tabular-nums">
            {index + 1}
          </span>
        )}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <MapPin size={11} strokeWidth={2} className="shrink-0 text-ink-muted" />
          <span className="truncate text-sm font-medium text-ink">{o.title}</span>
        </div>
        <div className="mt-0.5 text-[11px] text-ink-muted">
          {isDone && completed
            ? `Пройдено ${completed}`
            : isCancelled
              ? "Отменено"
              : schedule
                ? `Запланировано на ${schedule}`
                : "Запланировано"}
        </div>
        {o.mentor_comment && (
          <p className="mt-1.5 rounded bg-white px-2 py-1.5 text-[11px] italic text-ink-soft ring-1 ring-line">
            «{o.mentor_comment}»
          </p>
        )}
      </div>
    </li>
  );
}
